import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Button, Col, Container, Row} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import './dispatcher-dashboard.scss';
import Sidebar from './components/sidebar';
import JourneyCard from './components/journey-card';
import {IRootState} from 'app/shared/reducers';
import {getPatientCasesByStatusAndDispatchCenter} from 'app/entities/patient-case/patient-case.reducer';
import {RouteComponentProps, useLocation} from 'react-router-dom';
import {JourneyStatus} from 'app/shared/model/enumerations/journey-status.model';
import {IDispatcherDashboardCase} from 'app/shared/model/dispatcher-dashboard-case.model';
import {convertDateTimeFromServer, displaySecondsAsHoursMinutes} from 'app/shared/util/date-utils';
import moment from 'moment';
import {hasAnyPermission} from "app/shared/auth/permissions";
import {getMMamaSettings} from 'app/modules/administration/mmama-settings/mmama-settings.reducer';
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';

export interface IDispatcherDashboardProps extends StateProps, DispatchProps, RouteComponentProps {
}

export const DispatcherDashboard = (props: IDispatcherDashboardProps) => {
  const {patientCases, loading, account, userCountryId, isAdmin, mmamaSettings} = props;
  const location = useLocation();
  const [scheduledJourneys, setScheduledJourneys] = useState<IDispatcherDashboardCase[]>([]);
  const [inProgressJourneys, setInProgressJourneys] = useState<IDispatcherDashboardCase[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<string>(null);
  const [selectedCaseStatus, setSelectedCaseStatus] = useState<string>(null);
  const [countrySettings, setCountrySettings] = useState<IMMamaSettings | null>(null);

  // Initialize state from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status');
    const countryIdParam = urlParams.get('countryId');

    // Set status from URL or default to 'OPEN'
    setSelectedCaseStatus(statusParam || 'OPEN');

    if (countryIdParam) {
      setSelectedCountryId(countryIdParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (!props.mmamaSettings || props.mmamaSettings.length === 0) {
      props.getMMamaSettings();
    }
  }, [props.mmamaSettings, props.getMMamaSettings]);

  // Update country settings when selectedCountryId or userCountryId changes
  useEffect(() => {
    if (mmamaSettings && mmamaSettings.length > 0) {
      const currentCountryId = selectedCountryId || userCountryId;
      if (currentCountryId) {
        const settings = mmamaSettings.find(setting => setting.countryId === currentCountryId);
        if (settings) {
          setCountrySettings(settings);
        }
      }
    }
  }, [mmamaSettings, selectedCountryId, userCountryId]);

  // Function to load patient cases only
  const loadPatientCases = (caseStatus: string, countryId?: string, dispatchCenterId?: string) => {
    if (caseStatus === 'PENDING_FEEDBACK') {
      // For feedback pending, use pendingFeedback=true
      if (dispatchCenterId) {
        props.getPatientCasesByStatusAndDispatchCenter([], dispatchCenterId, countryId, true);
      } else if (countryId) {
        props.getPatientCasesByStatusAndDispatchCenter([], undefined, countryId, true);
      }
    } else {
      // For regular case statuses
      if (dispatchCenterId) {
        props.getPatientCasesByStatusAndDispatchCenter([caseStatus], dispatchCenterId, countryId);
      } else if (countryId) {
        props.getPatientCasesByStatusAndDispatchCenter([caseStatus], undefined, countryId);
      }
    }
  };

  // Load cases based on user role and case status changes
  useEffect(() => {
    // Only load if selectedCaseStatus is set
    if (!selectedCaseStatus) return;

    // Skip loading PENDING_FEEDBACK cases if dispatcher feedback is disabled
    if (selectedCaseStatus === 'PENDING_FEEDBACK' && !countrySettings?.enableDispatcherFeedback) return;

    if (isAdmin) {
      // For admin users: use selectedCountryId from dropdown (if available)
      if (selectedCountryId) {
        loadPatientCases(selectedCaseStatus, selectedCountryId);
      }
    } else {
      // For non-admin users: prioritize dispatchCenterId, fallback to userCountryId
      if (account?.dispatchCenterId) {
        loadPatientCases(selectedCaseStatus, undefined, account.dispatchCenterId);
      } else if (userCountryId) {
        loadPatientCases(selectedCaseStatus, userCountryId);
      }
    }
  }, [selectedCaseStatus, selectedCountryId, account?.dispatchCenterId, userCountryId, account?.roles, countrySettings?.enableDispatcherFeedback]);

  useEffect(() => {
    if (patientCases && patientCases.length > 0) {
      if (selectedCaseStatus === 'OPEN') {
        // For OPEN cases, filter by journey status to separate scheduled vs in-progress
        const scheduledStatuses = [
          JourneyStatus.NEEDS_DRIVER,
          JourneyStatus.NEEDS_VEHICLE_TYPE,
          JourneyStatus.NEEDS_DESTINATION,
          JourneyStatus.NEEDS_SOURCE
        ];
        const inProgressStatuses = [
          JourneyStatus.AWAITING_PICKUP,
          JourneyStatus.AWAITING_DROP_OFF
        ];
        setScheduledJourneys(patientCases.filter(item => scheduledStatuses.includes(item.journeyStatus)));
        setInProgressJourneys(patientCases.filter(item => inProgressStatuses.includes(item.journeyStatus)));
      } else {
        // For non-OPEN cases, show all journeys (already ordered by the backend)
        setScheduledJourneys([...patientCases]);
        setInProgressJourneys([]);
      }
    } else {
      setScheduledJourneys([]);
      setInProgressJourneys([]);
    }
  }, [patientCases, selectedCaseStatus]);

  const getCaseStatusDisplayName = (status: string) => {
    switch (status) {
      case 'OPEN':
        return 'Open Cases';
      case 'READY_FOR_PAYMENT':
        return 'Ready for Payment';
      case 'FOLLOW_UP':
        return 'Follow-up Cases';
      case 'PENDING_PAYMENT':
        return 'Pending Payment';
      case 'PENDING_FEEDBACK':
        return 'Feedback Pending';
      default:
        return 'Cases';
    }
  };

  const navigateToJourney = (patientCaseId: string) => {
    const currentParams = new URLSearchParams(location.search);
    const newParams = new URLSearchParams();
    newParams.set('patientCaseId', patientCaseId);

    // Preserve current country and status
    if (currentParams.get('countryId')) {
      newParams.set('countryId', currentParams.get('countryId'));
    }
    if (currentParams.get('status')) {
      newParams.set('status', currentParams.get('status'));
    }

    props.history.push(`/dispatcher-dashboard/journey?${newParams.toString()}`);
  };

  const getJourneyCardSubTitle = (item: IDispatcherDashboardCase): string => {
    let subTitle = '';
    if (item.motherEmergencyType) {
      subTitle += `Mother (${item.motherEmergencyType}) `;
    }
    if (item.babyEmergencyType) {
      subTitle += `Baby (${item.babyEmergencyType})`;
    }
    return subTitle;
  };

  const renderJourneyCard = (item: IDispatcherDashboardCase) => (
    <JourneyCard
      key={item.journeyId}
      status={item.journeyStatus}
      subtitle={getJourneyCardSubTitle(item)}
      location={item.startLocation || 'Unspecified'}
      destination={item.destinationLocation || 'Unspecified'}
      time={item.totalDurationSeconds ? displaySecondsAsHoursMinutes(item.totalDurationSeconds) : '-'}
      createdAgo={
        item.journeyCreatedAt
          ? moment.duration(moment().diff(moment(convertDateTimeFromServer(item.journeyCreatedAt)))).humanize()
          : ''
      }
      name={`${item.patientFirstName || ''} ${item.patientLastName || ''}`.trim()}
      onClick={() => navigateToJourney(item.patientCaseId)}
    />
  );

  return (
    <Container fluid>
      <Row>
        <Col md={3} className="bg-light">
          <Sidebar loading={loading}/>
        </Col>
        <Col md={9} className="p-3">
          {loading ? (
            <div className="text-center py-5">
              <div className="mt-3">Loading...</div>
            </div>
          ) : selectedCaseStatus === 'OPEN' ? (
            <Row>
              <Col md={6}>
                <h5>
                  {getCaseStatusDisplayName(selectedCaseStatus)} - Arranging Transport <span
                  className="text-primary">{scheduledJourneys.length}</span>
                </h5>
                {scheduledJourneys.length === 0 ? (
                  <div className="text-muted text-center py-4">No scheduled journeys found
                    for {getCaseStatusDisplayName(selectedCaseStatus).toLowerCase()}</div>
                ) : (
                  /* loop through scheduled journeys */
                  scheduledJourneys.map(renderJourneyCard)
                )}
              </Col>
              <Col md={6}>
                <h5>
                  {getCaseStatusDisplayName(selectedCaseStatus)} - Monitoring Transport <span
                  className="text-primary">{inProgressJourneys.length}</span>
                </h5>
                {inProgressJourneys.length === 0 ? (
                  <div className="text-muted text-center py-4">No journeys in progress
                    for {getCaseStatusDisplayName(selectedCaseStatus).toLowerCase()}</div>
                ) : (
                  /* loop through in progress journeys */
                  inProgressJourneys.map(renderJourneyCard)
                )}
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md={12}>
                <h5>
                  {getCaseStatusDisplayName(selectedCaseStatus)} <span
                  className="text-primary">{scheduledJourneys.length}</span>
                </h5>
                {scheduledJourneys.length === 0 ? (
                  <div className="text-muted text-center py-4">No journeys found
                    for {getCaseStatusDisplayName(selectedCaseStatus).toLowerCase()}</div>
                ) : (
                  /* loop through all journeys */
                  scheduledJourneys.map(renderJourneyCard)
                )}
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Button className="dispatcher-floating-button" onClick={() => {
        const urlParams = new URLSearchParams(location.search);
        const countryId = urlParams.get('countryId') || (isAdmin ? selectedCountryId : userCountryId);
        const registrationPath = countryId ? `/registration?countryId=${countryId}` : '/registration';
        props.history.push(registrationPath);
      }}>
        <FontAwesomeIcon icon="plus"/> Case
      </Button>
    </Container>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  patientCases: storeState.patientCase.dashboardCases,
  loading: storeState.patientCase.loading,
  account: storeState.authentication.account,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  userCountryId: storeState.authentication.userCountryId,
  mmamaSettings: storeState.mmamaSettings.settings,
});

const mapDispatchToProps = {
  getPatientCasesByStatusAndDispatchCenter,
  getMMamaSettings,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(DispatcherDashboard);
